import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { store } from '../../../lib/store'
import { expandLeftToolbar, collapseLeftToolbar } from '../../../action/left-toolbar-state-actions'
import { resolveTrackColor, normalizeTrackWidth } from '../../../lib/trackColor'

const SRC = 'bm-tracks-src'
// One layer per line style over a single shared source — line-dasharray can't be
// data-driven, so each style gets its own layer (filtered by _style) while color and
// width stay data-driven. Layer count is fixed at 3 regardless of track count.
const LAYERS = { solid: 'bm-tracks-solid', dashed: 'bm-tracks-dashed', dots: 'bm-tracks-dots' }
const LAYER_IDS = [LAYERS.solid, LAYERS.dashed, LAYERS.dots]

// Collect every track's LineString into a single FeatureCollection, tagging each with
// its id + resolved color/style/width so the 3D map matches the 2D styling.
function buildFC(tracks) {
  const features = []
  for (const track of tracks) {
    let geoJson
    try { geoJson = track.geoJson && JSON.parse(track.geoJson) } catch { continue }
    if (!geoJson) continue
    const feat = geoJson.features?.[0]
    if (!feat) continue
    const coords = feat.geometry?.coordinates
    if (!Array.isArray(coords) || coords.length < 2) continue
    const line = feat.properties?.line || {}
    const style = line.style === 'dashed' || line.style === 'dots' ? line.style : 'solid'
    features.push({
      ...feat,
      properties: {
        ...feat.properties,
        _id: track.id,
        _color: resolveTrackColor(line),
        _style: style,
        _width: normalizeTrackWidth(line.width)
      }
    })
  }
  return { type: 'FeatureCollection', features }
}

// Faint full-track "ghost" shown under the kept range while trimming (edit preview).
const GHOST_SRC = 'bm-tracks-ghost-src'
const GHOST_LAYER = 'bm-tracks-ghost'

function setGhost(m, coords) {
  if (!m || typeof m.getSource !== 'function') return
  if (Array.isArray(coords) && coords.length > 1) {
    const data = { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} }
    if (m.getSource(GHOST_SRC)) {
      m.getSource(GHOST_SRC).setData(data)
    } else {
      m.addSource(GHOST_SRC, { type: 'geojson', data })
      // Insert below the track layers so the kept range draws on top.
      const before = m.getLayer(LAYERS.solid) ? LAYERS.solid : undefined
      m.addLayer({
        id: GHOST_LAYER, type: 'line', source: GHOST_SRC,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#ffffff', 'line-opacity': 0.3, 'line-width': 2 }
      }, before)
    }
  } else {
    if (m.getLayer(GHOST_LAYER)) m.removeLayer(GHOST_LAYER)
    if (m.getSource(GHOST_SRC)) m.removeSource(GHOST_SRC)
  }
}

/**
 * Live-preview an in-progress track edit on the 3D map without writing to redux.
 * Rebuilds the source from the current tracks and overrides the edited feature's
 * color/style/width/geometry; `fullCoords` draws a faint ghost while trimming.
 * Call resetTracks3D() to revert.
 */
export function applyTrackPreview3D(map, id, preview) {
  if (!map || typeof map.getSource !== 'function' || !map.getSource(SRC)) return
  const { color, style, width, coords, fullCoords } = preview || {}
  const fc = buildFC(store.getState().tracks)
  const feat = fc.features.find(f => String(f.properties._id) === String(id))
  if (feat) {
    if (color) feat.properties._color = color
    if (style) feat.properties._style = style
    if (width != null) feat.properties._width = width
    if (Array.isArray(coords) && coords.length > 1) {
      feat.geometry = { ...feat.geometry, coordinates: coords }
    }
  }
  map.getSource(SRC).setData(fc)
  setGhost(map, fullCoords)
}

// Revert any preview — rebuild the main source from redux and drop the ghost.
export function resetTracks3D(map) {
  if (!map || typeof map.getSource !== 'function') return
  if (map.getSource(SRC)) map.getSource(SRC).setData(buildFC(store.getState().tracks))
  setGhost(map, null)
}

// Drop only the ghost (used on save — the previewed styling already matches what
// redux will re-render once the update lands, so leave the main source as-is).
export function clearTrackGhost3D(map) {
  setGhost(map, null)
}

function teardown(m) {
  if (!m) return
  if (m.getLayer(GHOST_LAYER)) m.removeLayer(GHOST_LAYER)
  if (m.getSource(GHOST_SRC)) m.removeSource(GHOST_SRC)
  LAYER_IDS.forEach(id => { if (m.getLayer(id)) m.removeLayer(id) })
  if (m.getSource(SRC)) m.removeSource(SRC)
}

const Render3dTracks = ({ map, tracks }) => {
  const activeMapRef = useRef(null)
  const mapClickRef = useRef(null)

  useEffect(() => {
    if (!map) return

    if (activeMapRef.current && activeMapRef.current !== map) {
      if (mapClickRef.current) {
        try { activeMapRef.current.off('click', mapClickRef.current) } catch (_) {}
        mapClickRef.current = null
      }
      teardown(activeMapRef.current)
    }
    activeMapRef.current = map

    // Adding a source before the style is ready throws, so the work is gated.
    // This used to be implicit: the style-key remount was deferred to idle, which
    // meant every track waited for a full tile settle before it could draw.
    const apply = () => {
      const fc = buildFC(tracks)

      if (map.getSource(SRC)) {
        map.getSource(SRC).setData(fc)
      } else {
        map.addSource(SRC, { type: 'geojson', data: fc })

        const base = {
          source: SRC,
          paint: {
            'line-color': ['get', '_color'],
            'line-width': ['get', '_width'],
            'line-opacity': 0.9
          }
        }
        // Solid — also the fallback for any unknown/missing style.
        map.addLayer({
          id: LAYERS.solid, type: 'line', ...base,
          filter: ['==', ['get', '_style'], 'solid'],
          layout: { 'line-join': 'round', 'line-cap': 'round' }
        })
        // Dashed — dash + gap scale with the line width (dasharray units are line-widths).
        map.addLayer({
          id: LAYERS.dashed, type: 'line', source: SRC,
          filter: ['==', ['get', '_style'], 'dashed'],
          layout: { 'line-join': 'round', 'line-cap': 'butt' },
          paint: { ...base.paint, 'line-dasharray': [2, 2] }
        })
        // Dots — zero-length dashes with round caps render as round dots.
        map.addLayer({
          id: LAYERS.dots, type: 'line', source: SRC,
          filter: ['==', ['get', '_style'], 'dots'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { ...base.paint, 'line-dasharray': [0, 2] }
        })

        const enterHandler = () => { map.getCanvas().style.cursor = 'pointer' }
        const leaveHandler = () => { map.getCanvas().style.cursor = '' }
        LAYER_IDS.forEach(id => {
          map.on('mouseenter', id, enterHandler)
          map.on('mouseleave', id, leaveHandler)
        })
      }

      // Map-level click with a ±10px query tolerance so clicking near — not exactly on —
      // a thin track line reliably opens the details panel. Re-bound each run so the
      // handler closure stays fresh.
      if (mapClickRef.current) {
        map.off('click', mapClickRef.current)
      }
      const mapClickHandler = (e) => {
        const layers = LAYER_IDS.filter(id => map.getLayer(id))
        if (!layers.length) return
        let features
        try {
          features = map.queryRenderedFeatures(
            [[e.point.x - 10, e.point.y - 10], [e.point.x + 10, e.point.y + 10]],
            { layers }
          )
        } catch (_) { return }
        if (!features || features.length === 0) return

        const id = features[0].properties._id
        const t = store.getState().tracks.find(t => String(t.id) === String(id))
        if (!t) return

        store.dispatch(collapseLeftToolbar())
        store.dispatch(expandLeftToolbar({ dialog: 'edit', editable: { ...t, measureType: 'track' } }))
      }
      map.on('click', mapClickHandler)
      mapClickRef.current = mapClickHandler
    }

    apply()
    // Re-add on style.load, which fires as soon as the new style is parsed —
    // addSource only needs that, not the fully-loaded style isStyleLoaded() waits
    // for. The styleKey remount still happens later at idle; apply is idempotent
    // (setData when the source already exists) so running twice is harmless.
    map.on('style.load', apply)
    return () => map.off('style.load', apply)
  }, [map, tracks])

  // Clean up the map-level click handler + layers/source on unmount.
  useEffect(() => {
    return () => {
      const m = activeMapRef.current
      if (m && mapClickRef.current) {
        try { m.off('click', mapClickRef.current) } catch (_) {}
      }
      teardown(m)
    }
  }, [])

  return null
}

const mapStateToProps = state => ({ tracks: state.tracks })
export default connect(mapStateToProps)(Render3dTracks)
